import api from '../lib/httpClient';
import type {
  WhitelistResponse,
  WhitelistUser
} from '../interfaces/apiInterfaces/auth';
import type {
  WhitelistUserParams,
  CreateWhitelistUserRequest,
  UpdateWhitelistUserRequest,
} from '../interfaces/apiInterfaces/whitelist';
import type { DisciplinesResponse } from '../interfaces/apiInterfaces/disciplines';
import type { RolesResponse } from '../interfaces/apiInterfaces/roles';

export const whitelistService = {
  /**
   * Check if a user email exists in the whitelist
   * @param email - User email to check
   * @returns WhitelistUser if found, null if not found
   * @throws Error if API call fails
   */
  async checkUserWhitelist(email: string): Promise<WhitelistUser | null> {
    try {
      const response = await api.get<WhitelistResponse>('/v1/whitelist-users', {
        params: { search_text: email }
      });

      // If data array is empty, user is not whitelisted
      if (response.data.data.length === 0) {
        return null;
      }

      // Return the first user (email should be unique)
      const user = response.data.data[0];

      // Check if user is soft-deleted
      if (user.user_is_deleted) {
        return null;
      }

      return user;
    } catch (error) {
      console.error('Error checking whitelist:', error);
      throw error;
    }
  },

  /**
   * Get all whitelisted users (with pagination and optional email filter)
   */
  async getAllWhitelistUsers(params?: WhitelistUserParams): Promise<WhitelistResponse> {
    try {
      const response = await api.get<WhitelistResponse>('/v1/whitelist-users', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching whitelist users:', error);
      throw error;
    }
  },

  /**
   * Create a new whitelisted user
   * @param data - User data to create
   */
  async createWhitelistUser(data: CreateWhitelistUserRequest): Promise<WhitelistUser> {
    try {
      const response = await api.post<WhitelistUser>('/v1/whitelist-users', data);
      return response.data;
    } catch (error: any) {
      console.error('Error creating whitelist user:', error);
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw error;
    }
  },

  /**
   * Delete a whitelisted user
   * @param userId - ID of the user to delete
   */
  async deleteWhitelistUser(userId: string | number): Promise<void> {
    try {
      await api.delete(`/v1/whitelist-users/${userId}`);
    } catch (error) {
      console.error('Error deleting whitelist user:', error);
      throw error;
    }
  },

  /**
   * Update a whitelisted user
   * @param userId - ID of the user to update
   * @param data - User data to update
   */
  async updateWhitelistUser(userId: string | number, data: UpdateWhitelistUserRequest): Promise<WhitelistUser> {
    try {
      const response = await api.put<WhitelistUser>(`/v1/whitelist-users/${userId}`, data);
      return response.data;
    } catch (error: any) {
      console.error('Error updating whitelist user:', error);
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw error;
    }
  },

  /**
   * Get all disciplines
   */
  async getDisciplines(page_number = 1, page_size = 200): Promise<DisciplinesResponse> {
    try {
      const response = await api.get<DisciplinesResponse>('/v1/disciplines', {
        params: { page_number, page_size }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching disciplines:', error);
      throw error;
    }
  },

  /**
   * Get all roles
   */
  async getRoles(page_number = 1, page_size = 100): Promise<RolesResponse> {
    try {
      const response = await api.get<RolesResponse>('/v1/roles', {
        params: { page_number, page_size }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching roles:', error);
      throw error;
    }
  },
};
